import type * as React from 'react'
import Image from 'next/image'
import { canOptimizeImageSrc } from '@/lib/image-optimizable'

type Sized = { width: number; height: number; fill?: never; sizes?: never }
type Filled = { fill: true; sizes: string; width?: never; height?: never }

/**
 * FRIDGE-1506: a stored or hotlinked image drawn smaller than its file. Stored
 * brand logos run to thousands of pixels (/brand-logos/crosley-logo.jpg is
 * 3,095 × 3,543 and 4.6 MB, drawn 35 × 40 in the collections directory) and
 * CPSC recall photos are hotlinked at up to 836 KB with no caching; through
 * the image optimizer the browser gets a cached file sized to the slot. A
 * source the optimizer refuses (an unlisted host, or a stored path with a
 * cache-busting query) stays a plain lazy img, as before. `fill` needs a
 * positioned parent, as next/image's does.
 */
export function OptimizedImg(props: { src: string; alt: string; className?: string; style?: React.CSSProperties } & (Sized | Filled)) {
  const { src, alt, className, style } = props
  if (canOptimizeImageSrc(src)) {
    return props.fill
      ? <Image src={src} alt={alt} fill sizes={props.sizes} className={className} />
      : <Image src={src} alt={alt} width={props.width} height={props.height} className={className} style={style} />
  }
  return props.fill
    // eslint-disable-next-line @next/next/no-img-element -- a source next/image may not optimize
    ? <img src={src} alt={alt} loading="lazy" decoding="async" className={`absolute inset-0 h-full w-full ${className ?? ''}`} />
    // eslint-disable-next-line @next/next/no-img-element -- a source next/image may not optimize
    : <img src={src} alt={alt} width={props.width} height={props.height} loading="lazy" decoding="async" className={className} style={style} />
}
